import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { computed, observable } from 'mobx';
import { WordStore, WordDerivationStore } from '@seedlang/stores';
import { isPresent } from '@seedlang/utils';
import Text from 'components/text';
import Modal from 'components/modal';
import SearchMultiSelect from 'components/form/search_multi_select';
import RelatedWordTableRow from 'pages/builder/words/related_word_table_row';
import autobind from 'autobind-decorator';

const Wrapper = styled.div`
  .related-words-table {
    width: 100%;
    margin-top: 10px;
  }
`;

@observer
class RelatedWordsEdit extends React.Component {
  @observable showEditRelatedWords = false;

  @autobind afterChange() {
    if (this.props.afterChange) {
      this.props.afterChange();
    }
  }

  @computed get word() {
    return this.props.word || WordStore.showData;
  }

  @computed get wordDerivationStems() {
    return this.word.wordDerivationStems || [];
  }

  @computed get wordDerivationChildren() {
    return this.word.wordDerivationChildren || [];
  }

  @computed get hasRelatedWords() {
    const nonSimpleStems = this.wordDerivationStems.filter(item => !item.simple);
    const nonSimpleChildren = this.wordDerivationChildren.filter(item => !item.simple);
    return nonSimpleStems.length > 0 || nonSimpleChildren.length > 0;
  }

  @computed get wordDerivationStemsOptions() {
    return this.word && isPresent(this.wordDerivationStems)
      ? this.wordDerivationStems.map(item => this.wordToMultiSearchOption(item))
      : [];
  }

  @computed get wordDerivationChildrenOptions() {
    return this.word && isPresent(this.wordDerivationChildren)
      ? this.wordDerivationChildren.map(item => this.wordToMultiSearchOption(item))
      : [];
  }

  @autobind wordToMultiSearchOption(word) {
    return {
      id: word.id,
      name: word && word.targetTextWithDefiniteArticle,
      targetWithWordTypeParentInfinitiveAndSource: word && word.targetWithWordTypeParentInfinitiveAndSource,
    };
  }

  @autobind onCreateWordDerivation(params = {}) {
    WordDerivationStore.create({data: {stemId: params['stemId'], childId: params['childId']}}, this.afterChange);
  }

  @autobind onDestroyWordDerivation(params = {}) {
    WordDerivationStore.destroy({ids: {stemId: params['stemId'], childId: params['childId']}}, this.afterChange);
  }

  @autobind clickShowEditRelatedWords() {
    this.showEditRelatedWords = true;
  }

  render() {
    if (!this.word.root) {
      return null;
    }

    const nonSimpleStems = this.wordDerivationStems.filter(item => !item.simple);
    const nonSimpleChildren = this.wordDerivationChildren.filter(item => !item.simple);

    return (
      <Wrapper>
        {
          this.showEditRelatedWords &&
            <Modal
              onCloseModal={() => this.showEditRelatedWords = false}
              gradient
              title='Manage Related Words'
            >
              <div style={{display: 'flex', flexDirection: 'column', height: '600px'}}>
                <Text heading={3}>Stems</Text>
                <Text small>Words from which <i>{this.word.targetText}</i> was build.</Text>
                <SearchMultiSelect
                  options={this.wordDerivationStemsOptions}
                  excludeIds={[this.word.id]}
                  onCreate={word => this.onCreateWordDerivation({childId: this.word.id, stemId: word.id})}
                  onDestroy={stemId => this.onDestroyWordDerivation({childId: this.word.id, stemId: stemId})}
                  searchStore={WordStore}
                  searchFilters={[{key: 'root', value: true}, {key: 'language_id', value: this.word.languageId}]}
                  searchField='target_text'
                  displayField='targetWithWordTypeParentInfinitiveAndSource'
                  sort='target_text'
                  linkTo='builder.words.edit'
                  linkId='wordId'
                />
                <Text heading={3} margin='20px 0 0 0'>Children</Text>
                <Text small>Words which "contain" or were build with <i>{this.word.targetText}</i> as a stem.</Text>
                <SearchMultiSelect
                  options={this.wordDerivationChildrenOptions}
                  excludeIds={[this.word.id]}
                  onCreate={word => this.onCreateWordDerivation({stemId: this.word.id, childId: word.id})}
                  onDestroy={childId => this.onDestroyWordDerivation({stemId: this.word.id, childId: childId})}
                  searchStore={WordStore}
                  searchFilters={[{key: 'root', value: true}, {key: 'language_id', value: this.word.languageId}]}
                  searchField='target_text'
                  displayField='targetWithWordTypeParentInfinitiveAndSource'
                  sort='target_text'
                  linkTo='builder.words.edit'
                  linkId='wordId'
                  keepInputOnSelect
                />
              </div>
            </Modal>
        }
        <div className='row'>
          <div className='col-xs-12'>
            <fieldset>
              <legend>Related Words</legend>
              <table className='table-wrapper related-words-table'>
                <thead>
                  <tr>
                    <th width='20'></th>
                    <th>Target</th>
                    <th>Sources</th>
                    <th>Relationship</th>
                    <th>Sentences</th>
                    <th>Word type</th>
                    <th>Public</th>
                    <th width='20'></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    nonSimpleStems.map(item => {
                      return (
                        <RelatedWordTableRow
                          key={`stem-${item.id}`}
                          relatedWord={item}
                          referenceWordId={this.word.id}
                          isStem
                          onDelete={this.onDestroyWordDerivation}
                        />
                      );
                    })
                  }
                  {
                    nonSimpleChildren.map(item => {
                      return (
                        <RelatedWordTableRow
                          key={`child-${item.id}`}
                          relatedWord={item}
                          referenceWordId={this.word.id}
                          isStem={false}
                          onDelete={this.onDestroyWordDerivation}
                        />
                      );
                    })
                  }
                  <tr
                    style={{cursor: 'pointer'}}
                  >
                    <td
                      colSpan='5'
                      onClick={this.clickShowEditRelatedWords}
                    >
                      <i className='fa fa-plus' style={{margin: '3px'}}/>
                      <Text fontSize='14px'>Edit Related Words</Text>
                    </td>
                  </tr>
                </tbody>
              </table>
            </fieldset>
          </div>
        </div>
      </Wrapper>
    );
  }
}

export default RelatedWordsEdit;